/**
 * VoiceMARModal — caregiver dictates med pass results; AI matches each
 * utterance to a real scheduled dose and we POST status updates for the
 * picked actions.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2, Sparkles, X, Check, CheckCircle2, XCircle, PauseCircle, AlertOctagon, AlertTriangle } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

const STATUS_META = {
  given: { l: "Given", icon: CheckCircle2, color: "#22C55E" },
  refused: { l: "Refused", icon: XCircle, color: "#EF4444" },
  held: { l: "Held", icon: PauseCircle, color: "#F59E0B" },
  missed: { l: "Missed", icon: AlertOctagon, color: "#EF4444" },
};

export default function VoiceMARModal({ open, clientId, dateStr, onClose, onApplied }) {
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState({});

  const rec = useVoiceRecorder({
    endpoint: "/ai/voice-fill/mar",
    extra: { client_id: clientId },
    filenamePrefix: "mar",
    timeoutMs: 90000,
    onResult: (data) => {
      setResult(data);
      const sel = {};
      (data.actions || []).forEach((a, i) => { sel[i] = true; });
      setSelected(sel);
    },
  });

  const close = () => {
    rec.reset();
    setResult(null);
    setSelected({});
    onClose?.();
  };

  const apply = async () => {
    if (!result?.actions?.length) return;
    setApplying(true);
    const today = dateStr || new Date().toISOString().slice(0, 10);
    const tasks = result.actions
      .filter((_, i) => selected[i])
      .map((a) =>
        api.post("/mar/log", {
          medication_id: a.medication_id,
          scheduled_date: today,
          scheduled_time: a.scheduled_time,
          status: a.status,
          notes: a.notes || "",
        }).then(() => ({ ok: true, a }))
          .catch((e) => ({ ok: false, a, err: e?.response?.data?.detail }))
      );
    const results = await Promise.all(tasks);
    const ok = results.filter((r) => r.ok).length;
    const fail = results.length - ok;
    if (ok) toast.success(`Logged ${ok} medication action${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`${fail} action${fail === 1 ? "" : "s"} could not be logged`);
    setApplying(false);
    onApplied?.();
    close();
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="voice-mar-modal">
      <div className="card-surface w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#16A34A1a", color: "#16A34A" }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice MAR</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Say what you gave / refused — AI matches to today's MAR."}
                {rec.isRecording && `Recording · ${rec.mmss}`}
                {rec.isProcessing && "Matching meds…"}
                {result && "Review and apply"}
              </p>
            </div>
          </div>
          <button type="button" onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-mar-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-4 py-6">
              {rec.isIdle && (
                <p className="text-sm text-center text-clinical-subtext max-w-md">
                  Example: <i>"Gave Margaret her 8am Lisinopril, she refused her morning
                  Donepezil because her stomach was upset."</i>
                </p>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-red-500 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-mar-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />
                  <p className="text-sm text-clinical-subtext">Matching to today's scheduled doses…</p>
                </>
              )}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-mar-stop" : "voice-mar-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop & process" : rec.isProcessing ? "Processing…" : "Start recording"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-mar-results">
              {result.transcript && (
                <details className="card-surface p-3 bg-clinical-bg/40">
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-clinical-subtext">
                    Transcript
                  </summary>
                  <p className="mt-2 text-sm text-clinical-text whitespace-pre-line">{result.transcript}</p>
                </details>
              )}
              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={12} /> AI warnings
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {(result.actions || []).length === 0 ? (
                <div className="card-surface p-4 text-center text-sm text-clinical-subtext">
                  No medication actions detected. Try again with the med name + status.
                </div>
              ) : (
                <ul className="space-y-2">
                  {result.actions.map((a, i) => {
                    const meta = STATUS_META[a.status] || STATUS_META.given;
                    const Icon = meta.icon;
                    return (
                      <li key={i} className="card-surface p-3 flex items-start gap-3"
                        data-testid={`voice-mar-action-${i}`}>
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                          className="mt-1 h-4 w-4 rounded text-clinical-accent"
                          data-testid={`voice-mar-toggle-${i}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-clinical-text">{a.matched_name}</span>
                            <span className="text-xs text-clinical-subtext">@ {a.scheduled_time}</span>
                          </div>
                          {a.notes && (
                            <p className="mt-0.5 text-xs text-clinical-subtext italic">"{a.notes}"</p>
                          )}
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                          style={{ background: meta.color + "1a", color: meta.color }}>
                          <Icon size={12} /> {meta.l}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {result && (result.actions || []).length > 0 && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button type="button" onClick={() => { setResult(null); setSelected({}); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-mar-redo">
              Record again
            </button>
            <button type="button" onClick={apply} disabled={applying}
              className="btn-primary" data-testid="voice-mar-apply">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check size={14} />}
              Apply {Object.values(selected).filter(Boolean).length} action{Object.values(selected).filter(Boolean).length === 1 ? "" : "s"}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
